import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const AccessibilityContext = createContext();

// Default values for accessibility settings
const defaultSettings = {
  fontSizeScale: 1.0,
  isHighContrast: false,
};

// Provider component
export const AccessibilityProvider = ({ children }) => {
  // Initialize state from localStorage or use defaults
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('accessibility-settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  // Update localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    
    // Apply font size scaling to the root element
    document.documentElement.style.setProperty('--font-size-scale', settings.fontSizeScale);
    
    // Add a class to the html element for each font size step for more specific styling
    const fontSizeClass = `font-scale-${Math.round(settings.fontSizeScale * 10)}`;
    
    // Remove any existing font scale classes
    const htmlElement = document.documentElement;
    const existingClasses = [...htmlElement.classList].filter(cls => cls.startsWith('font-scale-'));
    existingClasses.forEach(cls => htmlElement.classList.remove(cls));
    
    // Add the new font scale class
    htmlElement.classList.add(fontSizeClass);
    
    // Apply high contrast mode
    if (settings.isHighContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
  }, [settings]);

  // Increase font size (with upper limit)
  const increaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSizeScale: Math.min(prev.fontSizeScale + 0.1, 1.3),
    }));
  };

  // Decrease font size (with lower limit)
  const decreaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSizeScale: Math.max(prev.fontSizeScale - 0.1, 0.9),
    }));
  };

  // Toggle high contrast mode
  const toggleHighContrast = () => {
    setSettings(prev => ({
      ...prev,
      isHighContrast: !prev.isHighContrast,
    }));
  };

  // Context value
  const value = {
    ...settings,
    increaseFontSize,
    decreaseFontSize,
    toggleHighContrast,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

// Custom hook for using the accessibility context
export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export default AccessibilityContext;
